import { delay } from "../client.js";

const STORAGE_KEY = "console-design.account-permissions.v2";

export const ACCOUNT_USER_ROLES = ["ADMIN", "EDITOR", "VIEWER"];

/** Seed rows match the permissions wireframe (same user may appear with multiple roles). */
const SEED_ACCOUNT_USERS = {
  "acc-1": [
    { id: "ausr-1", name: "Saksham Tulani", email: "sakshamtulani@intellicar.in", role: "ADMIN" },
    { id: "ausr-2", name: "Akash Rampure", email: "akash@intellicar.in", role: "ADMIN" },
    { id: "ausr-3", name: "Ayush Mhaskar", email: "ayushmhaskar@intellicar.in", role: "EDITOR" },
    { id: "ausr-4", name: "Ayush Mhaskar", email: "ayushmhaskar@intellicar.in", role: "VIEWER" },
    { id: "ausr-5", name: "Ayush Mhaskar", email: "ayushmhaskar@intellicar.in", role: "ADMIN" },
    { id: "ausr-6", name: "Akash Rampure", email: "akash@intellicar.in", role: "EDITOR" },
  ],
  "acc-2": [
    { id: "ausr-7", name: "Sonal Pachpute", email: "sonal@intellicar.in", role: "ADMIN" },
    { id: "ausr-8", name: "Aman Gandhi", email: "aman@intellicar.in", role: "EDITOR" },
    { id: "ausr-9", name: "Karan Aneja", email: "karan@intellicar.in", role: "VIEWER" },
  ],
  "acc-3": [
    { id: "ausr-10", name: "Debnath Mondal", email: "debnath@intellicar.in", role: "ADMIN" },
    { id: "ausr-11", name: "Rinoy K", email: "rinoy@intellicar.in", role: "EDITOR" },
  ],
  "acc-4": [
    { id: "ausr-12", name: "Chirag Kumar", email: "chirag@intellicar.in", role: "ADMIN" },
    { id: "ausr-13", name: "Joseph MJ", email: "joseph@intellicar.in", role: "VIEWER" },
  ],
};

function cloneSeed() {
  return Object.fromEntries(
    Object.entries(SEED_ACCOUNT_USERS).map(([id, users]) => [id, users.map((u) => ({ ...u }))]),
  );
}

function loadStore() {
  const merged = cloneSeed();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return merged;
    const parsed = JSON.parse(raw);
    for (const [accountId, users] of Object.entries(parsed)) {
      if (Array.isArray(users)) merged[accountId] = users;
    }
  } catch {
    /* ignore */
  }
  return merged;
}

function saveStore(store) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
}

function nextId() {
  return `ausr-${Date.now().toString(36)}`;
}

function getAccountUsers(store, accountId) {
  if (!store[accountId]) {
    store[accountId] = SEED_ACCOUNT_USERS[accountId]
      ? SEED_ACCOUNT_USERS[accountId].map((u) => ({ ...u }))
      : [];
  }
  return store[accountId];
}

export async function listAccountUsers(accountId) {
  await delay(140);
  const store = loadStore();
  return [...getAccountUsers(store, accountId)];
}

export async function updateAccountUserRole(accountId, userId, role) {
  await delay(100);
  if (!ACCOUNT_USER_ROLES.includes(role)) throw new Error("Invalid role");
  const store = loadStore();
  const users = getAccountUsers(store, accountId);
  const user = users.find((u) => u.id === userId);
  if (!user) throw new Error("User not found");
  if (users.some((u) => u.id !== userId && u.email === user.email && u.role === role)) {
    throw new Error("This user already has this role on the account");
  }
  user.role = role;
  saveStore(store);
  return { ...user };
}

export async function addAccountUser(accountId, payload) {
  await delay(120);
  const name = payload.name?.trim();
  const email = payload.email?.trim().toLowerCase();
  const role = payload.role ?? "VIEWER";
  if (!name) throw new Error("Name is required");
  if (!email) throw new Error("Email is required");
  if (!ACCOUNT_USER_ROLES.includes(role)) throw new Error("Invalid role");

  const store = loadStore();
  const users = getAccountUsers(store, accountId);
  if (users.some((u) => u.email.toLowerCase() === email && u.role === role)) {
    throw new Error("This user already has this role on the account");
  }

  const user = { id: nextId(), name, email, role };
  users.push(user);
  saveStore(store);
  return { ...user };
}
